import React, { useCallback, useContext, useState } from 'react'
import { CircularProgress, TextField } from '@material-ui/core'
import { ErrorOutlineRounded } from '@material-ui/icons'
import clsx from 'clsx'

import { BOOK_LABEL, readEmail } from '../../../../lib/pay-by-link'
import { customerMessage } from '../../../../lib/api-json'
import { browserHistory } from '../../../../services/history'
import { context, setIsProcessingStripe, useOrdersApi, useSelectors } from '../../../../store'
import { BOOKING_ROUTES, STEPS } from '../steps'

// PAY_BY_LINK (1 Nov 2026): the pay step with no card in it.
//
// ONE action, "Book now", and NOTHING about payment anywhere on this panel.
// Robin: "when user hits the book without pay form I dont think its an option
// I think we either say nothing or that we will share payment link, Im
// thinking nothing as it doesnt block anything" and "but its not book now, pay
// to confirm, its just book now lol". So there is no price-and-pay phrasing,
// no "we will email you a link", no helper line and no asterisk: announcing
// the arrangement would test people's reaction to the arrangement instead of
// measuring what dropping the card ask does, and it blocks nothing.
//
// The one field that has to be here is the email, because it is the BACKUP
// channel for the Checkout link (WhatsApp is the default) and the card path
// used to collect it inside Stripe's own form. It refuses nothing: an address
// we cannot read is simply not sent, the server reports a missing email and
// Aider asks for it (Order#missing_details).
//
// `cardFields` is rendered when the page put the postcode, house number and
// mobile in the card section rather than above the buttons
// (WALLET_ADDRESS_FROM_SHEET), so the arm works whichever way that switch is
// set.
export const PayByLinkPanel = ({ cardFields = null, onBeforePay = null, classes }) => {
  const { dispatch } = useContext(context)
  const { isProcessingStripe, user } = useSelectors()
  const { apiBookWithoutPayment } = useOrdersApi()
  // Prefilled from the account when it holds a readable address, and always
  // editable: the link has to reach the person who is booking.
  const [typedEmail, setTypedEmail] = useState(readEmail(user?.email) || '')
  const [error, setError] = useState('')

  // The press itself is already counted: onBeforePay fires
  // `booking_step_completed` (step_name book) in BOTH arms, and the server's
  // `booking_submitted` is the metric (ANALYTICS.md). Nothing new here.
  const book = useCallback(async () => {
    setError('')
    if (onBeforePay && (await onBeforePay('book')) === false) return

    dispatch(setIsProcessingStripe(true))
    try {
      const res = await apiBookWithoutPayment(readEmail(typedEmail))
      dispatch(setIsProcessingStripe(false))
      if (res?.status !== 'ok') {
        setError(customerMessage(res) || 'Sorry, we could not take that booking. Please try again.')
        return
      }
      // The channel the server actually reached them on rides to the success
      // screen, so it can say WhatsApp or email truthfully (WhatsApp is the
      // default since 1 Nov 2026 and email is the backup).
      //
      // THE CHANNEL ONLY, NEVER THE ADDRESS. The email used to ride in this
      // query string, which puts it in PostHog's $current_url, in the Referer
      // of every outbound link on the page and in the server log, against
      // this feature's own rule about customer data (review finding, 2 Nov
      // 2026). The screen says "we have emailed you a link" without it.
      const via = res.data?.link_channel
      browserHistory.push(
        [`${BOOKING_ROUTES[STEPS.FINISH]}?pending_payment=1`,
          via ? `via=${encodeURIComponent(via)}` : null].filter(Boolean).join('&')
      )
    } catch (err) {
      dispatch(setIsProcessingStripe(false))
      setError(customerMessage(err) || 'Sorry, we could not take that booking. Please try again.')
    }
  }, [apiBookWithoutPayment, dispatch, onBeforePay, typedEmail])

  return (
    <div className={classes.oneCheckout} data-testid="pay-by-link">
      <div className={classes.oneCard}>
        {cardFields}
        <div className={classes.field} data-testid="pay-by-link-email">
          <label className={classes.fieldLabel} htmlFor="book-email">Email</label>
          <TextField
            id="book-email"
            fullWidth
            placeholder="you@example.com"
            value={typedEmail}
            onChange={ev => { setTypedEmail(ev.target.value); if (error) setError('') }}
            InputProps={{ disableUnderline: true, autoComplete: 'email', type: 'email', inputMode: 'email' }}
          />
        </div>
        <button
          type="button"
          onClick={book}
          className={clsx(classes.cardCta, isProcessingStripe && classes.cardCtaDisabled)}
          disabled={!!isProcessingStripe}
          data-testid="pay-by-link-book">
          {isProcessingStripe ? 'Just a sec…' : BOOK_LABEL}
          {isProcessingStripe && <CircularProgress size={18} className={classes.btnSpinner} />}
        </button>
        {error && (
          <p className={classes.cardError} role="alert" data-testid="pay-by-link-error">
            <ErrorOutlineRounded fontSize="small" />
            <span>{error}</span>
          </p>
        )}
      </div>
    </div>
  )
}
